import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
    ArrowRight, Loader2, CheckCircle, Target, BookOpen, Gauge, 
    Layers, FileText, TrendingUp, Award, Brain, ChevronRight,
    Sparkles, BarChart3, PieChart
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHAPTER_1_MODULES = [
    { id: 'diferencial_inicial', name: 'Diferencial inicial', icon: Target, color: '#3E5F6E' },
    { id: 'registro_episodios', name: 'Registro de episodios', icon: BookOpen, color: '#5FAF9D' },
    { id: 'semaforo_funcional', name: 'Semáforo funcional', icon: Gauge, color: '#7A9BA8' },
    { id: 'doble_capa', name: 'Doble capa', icon: Layers, color: '#E8B27A' },
    { id: 'linea_base', name: 'Línea base', icon: FileText, color: '#5FAF9D' }
];

export default function ResumenCapitulo1() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, {
                    withCredentials: true
                });
                setProfile(data);
            } catch (error) {
                toast.error('Error al cargar datos');
                navigate('/usuario');
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [navigate]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    const completedModules = profile?.modules_completed || [];
    const chapter1Completed = CHAPTER_1_MODULES.filter(m => 
        completedModules.some(cm => cm.module_id === m.id)
    );
    const completionRate = (chapter1Completed.length / CHAPTER_1_MODULES.length) * 100;

    // Extract insights from AI summaries
    const aiSummaries = profile?.ai_summaries || [];
    const patronDominante = profile?.dominant_pattern || aiSummaries.find(s => s.patron_dominante)?.patron_dominante;
    const estadoFuncional = profile?.functional_state;
    const baseline = profile?.baseline;
    const tareaFaro = profile?.lighthouse_task || baseline?.tarea_faro;

    // Calculate pattern distribution for chart
    const patternCounts = {};
    aiSummaries.forEach(summary => {
        if (summary.patron_dominante) {
            patternCounts[summary.patron_dominante] = (patternCounts[summary.patron_dominante] || 0) + 1;
        }
    });

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <img src="/logo-senda.png" alt="SENDA" className="h-9 w-auto" />
                            <div>
                                <h1 className="text-xl font-semibold text-[#1F2A30] font-['Outfit']">Resumen Capítulo 1</h1>
                                <p className="text-sm text-[#4A5B63]">Tu perfil conductual consolidado</p>
                            </div>
                        </div>
                        <Link to="/usuario">
                            <Button variant="ghost" className="text-[#3E5F6E] hover:bg-[#3E5F6E]/5">
                                Volver al inicio
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#3E5F6E] via-[#3E5F6E] to-[#2F4A57] rounded-3xl p-8 lg:p-12 mb-8 shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#5FAF9D]/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#5FAF9D]/10 rounded-full blur-2xl"></div>
                    
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="h-6 w-6 text-[#5FAF9D]" />
                                <span className="text-[#5FAF9D] font-medium uppercase tracking-wide text-sm">Capítulo 1 Completado</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-light text-white font-['Outfit'] mb-4">
                                Tu línea base está lista
                            </h2>
                            <p className="text-white/80 text-lg max-w-xl">
                                Has completado el proceso de autoexploración inicial. Ahora tienes una base sólida para entender tus patrones de evitación.
                            </p>
                        </div>
                        
                        <div className="flex flex-col items-center">
                            <div className="relative w-40 h-40">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle
                                        cx="80" cy="80" r="70"
                                        fill="none"
                                        stroke="rgba(255,255,255,0.2)"
                                        strokeWidth="12"
                                    />
                                    <circle
                                        cx="80" cy="80" r="70"
                                        fill="none"
                                        stroke="#5FAF9D"
                                        strokeWidth="12"
                                        strokeLinecap="round"
                                        strokeDasharray={`${completionRate * 4.4} 440`}
                                        className="transition-all duration-1000"
                                    />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-4xl font-bold text-white font-['Outfit']">{Math.round(completionRate)}%</span>
                                    <span className="text-white/60 text-sm">completado</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <Card className="bg-white border-[#DCE3E8] shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-[#3E5F6E]/10 rounded-xl flex items-center justify-center">
                                    <BarChart3 className="h-6 w-6 text-[#3E5F6E]" />
                                </div>
                                <span className="text-3xl font-bold text-[#1F2A30] font-['Outfit']">{chapter1Completed.length}</span>
                            </div>
                            <p className="text-[#4A5B63] font-medium">Módulos completados</p>
                            <p className="text-sm text-[#7A8A93]">de {CHAPTER_1_MODULES.length} en el capítulo</p>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-white border-[#DCE3E8] shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-[#5FAF9D]/10 rounded-xl flex items-center justify-center">
                                    <Brain className="h-6 w-6 text-[#5FAF9D]" />
                                </div>
                                <span className="text-3xl font-bold text-[#1F2A30] font-['Outfit']">{aiSummaries.length}</span>
                            </div>
                            <p className="text-[#4A5B63] font-medium">Lecturas de avance</p>
                            <p className="text-sm text-[#7A8A93]">insights personalizados</p>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-white border-[#DCE3E8] shadow-sm hover:shadow-md transition-shadow">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-[#E8B27A]/10 rounded-xl flex items-center justify-center">
                                    <TrendingUp className="h-6 w-6 text-[#E8B27A]" />
                                </div>
                                <span className="text-3xl font-bold text-[#1F2A30] font-['Outfit']">{profile?.streak_days || 0}</span>
                            </div>
                            <p className="text-[#4A5B63] font-medium">Días de racha</p>
                            <p className="text-sm text-[#7A8A93]">consistencia mantenida</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Module Progress Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-[#1F2A30]">
                                <PieChart className="h-5 w-5 text-[#3E5F6E]" />
                                Progreso por módulo
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {CHAPTER_1_MODULES.map((module, index) => {
                                    const isCompleted = completedModules.some(cm => cm.module_id === module.id);
                                    const Icon = module.icon;
                                    return (
                                        <div key={module.id} className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                                isCompleted ? 'bg-[#5FAF9D]' : 'bg-[#DCE3E8]'
                                            }`}>
                                                {isCompleted ? (
                                                    <CheckCircle className="h-5 w-5 text-white" />
                                                ) : (
                                                    <Icon className="h-5 w-5 text-[#7A8A93]" />
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-sm font-medium ${isCompleted ? 'text-[#1F2A30]' : 'text-[#7A8A93]'}`}>
                                                        {module.name}
                                                    </span>
                                                    <span className={`text-xs ${isCompleted ? 'text-[#5FAF9D]' : 'text-[#7A8A93]'}`}>
                                                        {isCompleted ? '100%' : '0%'}
                                                    </span>
                                                </div>
                                                <div className="h-2 bg-[#DCE3E8] rounded-full overflow-hidden">
                                                    <div 
                                                        className="h-full bg-[#5FAF9D] transition-all duration-500"
                                                        style={{ width: isCompleted ? '100%' : '0%' }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Key Insights */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-[#1F2A30]">
                                <Sparkles className="h-5 w-5 text-[#5FAF9D]" />
                                Hallazgos clave
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {patronDominante && (
                                <div className="p-4 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#3E5F6E] uppercase mb-1">Patrón dominante</p>
                                    <p className="text-[#1F2A30] font-medium">{patronDominante}</p>
                                </div>
                            )}
                            
                            {estadoFuncional && (
                                <div className="p-4 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1">Estado funcional</p>
                                    <p className="text-[#1F2A30] font-medium capitalize">{estadoFuncional}</p>
                                </div>
                            )}
                            
                            {tareaFaro && (
                                <div className="p-4 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-xl border border-[#5FAF9D]/20">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1 flex items-center gap-1">
                                        <Target className="h-3 w-3" />
                                        Tarea faro
                                    </p>
                                    <p className="text-[#1F2A30] font-medium">{tareaFaro}</p>
                                </div>
                            )}
                            
                            {baseline?.actividad_sustituta && (
                                <div className="p-4 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#E8B27A] uppercase mb-1">Conducta sustituta frecuente</p>
                                    <p className="text-[#1F2A30]">{baseline.actividad_sustituta}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* CTA to Chapter 2 */}
                <Card className="bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] border-0 shadow-xl overflow-hidden">
                    <CardContent className="p-8 lg:p-12">
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                            <div className="text-center lg:text-left">
                                <div className="flex items-center justify-center lg:justify-start gap-2 mb-3">
                                    <div className="w-8 h-8 bg-[#5FAF9D] rounded-lg flex items-center justify-center">
                                        <span className="text-white font-bold text-sm">2</span>
                                    </div>
                                    <span className="text-[#5FAF9D] font-medium uppercase tracking-wide text-sm">Siguiente paso</span>
                                </div>
                                <h3 className="text-2xl lg:text-3xl font-light text-white font-['Outfit'] mb-2">
                                    Capítulo 2: Amenaza y Defensa
                                </h3>
                                <p className="text-white/70 max-w-lg">
                                    Profundiza en las amenazas internas que activan tu evitación y descubre los mecanismos de defensa que utilizas.
                                </p>
                            </div>
                            <Link to="/usuario">
                                <Button className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white px-8 py-4 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all text-lg">
                                    Comenzar Capítulo 2
                                    <ChevronRight className="h-5 w-5 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
